import { EarningsRange } from "@pax/api-contract/accounting/v1alpha1";
import { PresetId } from "@pax/api-contract/common";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { EarningsRangeTabs, PresetEarnings } from "@/features/earnings/PresetEarnings";
import { earningsByDayQuery } from "@/features/earnings/queries";
import { field, fieldLabel, Heading, Workspace } from "@/features/evals/components";
import { periodsBackFrom } from "@/features/ledger/charts/PeriodStepper";
import { Note } from "@/features/ledger/components";

interface EarningsSearch {
  /** One preset's earnings instead of everything the reader earned. */
  presetId?: string;
  range?: EarningsRange;
  /** How many ranges back from the newest. */
  back?: number;
}

export const Route = createFileRoute("/_authenticated/earnings")({
  validateSearch: (search: Record<string, unknown>): EarningsSearch => ({
    presetId: PresetId.safeParse(search.presetId).data,
    range: EarningsRange.safeParse(search.range).data,
    back: periodsBackFrom(search.back),
  }),
  staticData: { access: "account" },
  head: () => ({ meta: [{ title: "Preset earnings · Pax.co" }] }),
  component: EarningsPage,
});

/**
 * The creator rewards the reader earned, stacked by preset. With a preset picked, what that preset
 * paid out: split by recipient for its team, the reader's own share for anyone else it paid.
 */
function EarningsPage() {
  const { presetId, range = "30d", back = 0 } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [parts, setParts] = useState<string[]>();
  const mine = useQuery(earningsByDayQuery({}, range, back));
  const presets = (mine.data?.parts ?? []).filter(part => part.presetId);
  const picked = presets.find(part => part.presetId === presetId);
  const nothing = <Note>Nothing to show for this preset.</Note>;
  const step = (next: number) =>
    void navigate({
      search: { presetId, range: range === "30d" ? undefined : range, back: next || undefined },
      resetScroll: false,
    });
  const pick = (next: EarningsSearch) => {
    setParts(undefined);
    void navigate({ search: { ...next, range, back: back || undefined }, resetScroll: false });
  };
  return (
    <Workspace>
      <Heading title="Preset earnings" />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <EarningsRangeTabs
            range={range}
            onChange={next =>
              void navigate({
                search: { presetId, range: next === "30d" ? undefined : next },
                replace: true,
                resetScroll: false,
              })
            }
          />
          <label className="flex items-center gap-2">
            <span className={fieldLabel}>Preset</span>
            <select
              value={presetId ?? ""}
              onChange={event => pick({ presetId: event.target.value || undefined })}
              className={`w-64 max-w-full ${field}`}
            >
              <option value="">All my presets</option>
              {presetId && !picked && <option value={presetId}>This preset</option>}
              {presets.map(part => (
                <option key={part.presetId} value={part.presetId ?? ""}>
                  {part.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {presetId ? (
          <PresetEarnings
            key={presetId}
            filter={{ presetId }}
            range={range}
            back={back}
            onBackChange={step}
            parts={parts ?? []}
            onPartsChange={setParts}
            notFound={nothing}
          />
        ) : (
          <PresetEarnings
            filter={{}}
            range={range}
            back={back}
            onBackChange={step}
            parts={parts ?? []}
            onPartsChange={setParts}
          />
        )}
      </div>
    </Workspace>
  );
}
